import React, { useMemo, useState } from 'react';
import {
  Modal, Steps, Segmented, Select, DatePicker, Input, Button, Space, Typography,
  Alert, Checkbox, Radio, Empty, Spin, message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import {
  useCreateFreeSaveMutation,
  useListWeatherImportLocationsQuery,
  useListUserClimateMapsQuery,
} from '../../api/savesV2Api';
import { useListGardenPlansQuery } from '../../api/gardenPlansApi';
import type { WeatherImportLocation } from '../../types/simulation';
import UsLocationMap from './UsLocationMap';

const { Text, Paragraph } = Typography;

type ClimateKind = 'year' | 'random' | 'userCustom';

const MEASUREMENT_OPTIONS = [
  { label: 'Метры', value: 'metric' },
  { label: 'Футы', value: 'imperial' },
];

const DEFAULT_SAVE_NAME = 'Мой сейв';

export interface GuidedSaveCreationWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated: (saveId: string) => void;
}

const GuidedSaveCreationWizard: React.FC<GuidedSaveCreationWizardProps> = ({ open, onClose, onCreated }) => {
  const [step, setStep] = useState(0);
  const [locationKey, setLocationKey] = useState<string | null>(null);
  const [climateKind, setClimateKind] = useState<ClimateKind>('year');
  const [year, setYear] = useState<number | null>(null);
  const [seed, setSeed] = useState('');
  const [customMapId, setCustomMapId] = useState<string | null>(null);
  const [fromPlan, setFromPlan] = useState(false);
  const [planVersionId, setPlanVersionId] = useState<string | null>(null);
  const [planTargetYear, setPlanTargetYear] = useState(dayjs().year());
  const [applyPlantings, setApplyPlantings] = useState(true);
  const [name, setName] = useState(DEFAULT_SAVE_NAME);
  const [startDate, setStartDate] = useState<Dayjs>(dayjs());
  const [measurementSystem, setMeasurementSystem] = useState('metric');
  const [description, setDescription] = useState('');

  const { data: catalog, isFetching: catalogFetching } = useListWeatherImportLocationsQuery(undefined, { skip: !open });
  const locations = useMemo(() => catalog?.locations ?? [], [catalog]);
  const recentYears = useMemo(() => catalog?.recentCompleteYears ?? [], [catalog]);
  const selectedLocation = useMemo<WeatherImportLocation | null>(
    () => locations.find((l) => l.key === locationKey) ?? null,
    [locations, locationKey],
  );

  const { data: userMaps = [], isFetching: userMapsFetching } = useListUserClimateMapsQuery(
    selectedLocation ? { lat: selectedLocation.latitude, lon: selectedLocation.longitude } : undefined,
    { skip: !open || climateKind !== 'userCustom' },
  );

  const { data: gardenPlans = [], isFetching: plansFetching } = useListGardenPlansQuery(
    { status: 'active' },
    { skip: !open || !fromPlan },
  );
  const selectedPlan = useMemo(
    () => gardenPlans.find((plan) => plan.latestVersionId === planVersionId) || null,
    [gardenPlans, planVersionId],
  );

  const [createFreeSave, { isLoading: creating }] = useCreateFreeSaveMutation();

  const yearOptions = useMemo(() => {
    const current = dayjs().year();
    const years = Array.from(new Set([current, ...recentYears])).sort((a, b) => b - a);
    return years.map((y) => ({ value: y, label: y === current ? `${y} (текущий)` : String(y) }));
  }, [recentYears]);

  const reset = () => {
    setStep(0);
    setLocationKey(null);
    setClimateKind('year');
    setYear(null);
    setSeed('');
    setCustomMapId(null);
    setFromPlan(false);
    setPlanVersionId(null);
    setPlanTargetYear(dayjs().year());
    setApplyPlantings(true);
    setName(DEFAULT_SAVE_NAME);
    setStartDate(dayjs());
    setMeasurementSystem('metric');
    setDescription('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSelectLocation = (loc: WeatherImportLocation) => {
    setLocationKey(loc.key);
  };

  const handleYearChange = (value: number) => {
    setYear(value);
    // Align the save's start year with the chosen climate year (keep month/day).
    setStartDate((prev) => (prev ? prev.year(value) : dayjs(`${value}-04-15`)));
  };

  const climateValid =
    climateKind === 'year'
      ? true
      : climateKind === 'random'
        ? true
        : Boolean(customMapId);

  const canNext =
    step === 0 ? Boolean(locationKey)
      : step === 1 ? climateValid
        : step === 2 ? (!fromPlan || Boolean(planVersionId))
          : true;

  const buildClimateSource = (): Record<string, unknown> => {
    if (climateKind === 'random') return seed ? { kind: 'random', seed } : { kind: 'random' };
    if (climateKind === 'userCustom') return { kind: 'userCustom', customMapId };
    return year ? { kind: 'year', year } : { kind: 'year' };
  };

  const handleCreate = async () => {
    if (!locationKey) return;
    const payload: Record<string, unknown> = {
      name: name.trim() || DEFAULT_SAVE_NAME,
      description: description || '',
      locationKey,
      climateSource: buildClimateSource(),
    };
    if (fromPlan && planVersionId) {
      payload.plan = {
        versionId: planVersionId,
        targetYear: planTargetYear,
        layers: { topology: true, plantings: applyPlantings && Boolean(selectedPlan?.latestVersionSummary?.layers.plantings) },
      };
    } else {
      payload.startDate = startDate.format('YYYY-MM-DD');
      payload.measurementSystem = measurementSystem;
    }

    try {
      const result = await createFreeSave(payload).unwrap();
      const saveId = result?.save?.id;
      message.success('Сейв создан');
      reset();
      onClose();
      if (saveId) onCreated(saveId);
    } catch (err) {
      const e = err as { data?: { message?: string } };
      message.error(e?.data?.message || 'Не удалось создать сейв');
    }
  };

  const steps = [
    {
      title: 'Локация',
      content: (
        <div>
          <Paragraph type="secondary">
            Выберите город на карте США. Его координаты станут гео сейва, а климат —
            погодой этой локации. Это фиксируется при создании и потом не меняется.
          </Paragraph>
          {catalogFetching ? (
            <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>
          ) : (
            <>
              <UsLocationMap locations={locations} selectedKey={locationKey} onSelect={handleSelectLocation} />
              <Space style={{ marginTop: 12 }} direction="vertical" size={4} align="start">
                <Select
                  style={{ width: 320 }}
                  placeholder="…или выбрать из списка"
                  value={locationKey ?? undefined}
                  onChange={(v) => setLocationKey(v)}
                  options={locations.map((l) => ({ value: l.key, label: l.name }))}
                  showSearch
                  optionFilterProp="label"
                />
                {selectedLocation && (
                  <Text type="secondary">{selectedLocation.region || selectedLocation.name}</Text>
                )}
              </Space>
            </>
          )}
        </div>
      ),
    },
    {
      title: 'Климат',
      content: (
        <div>
          <Segmented
            block
            value={climateKind}
            onChange={(v) => setClimateKind(v as ClimateKind)}
            options={[
              { label: 'Исторический год', value: 'year' },
              { label: 'Случайный год', value: 'random' },
              { label: 'Моя карта', value: 'userCustom' },
            ]}
          />
          <div style={{ marginTop: 16 }}>
            {climateKind === 'year' && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Text>Год климатической нормы локации:</Text>
                <Select
                  style={{ width: 220 }}
                  placeholder="Выберите год"
                  value={year ?? undefined}
                  onChange={handleYearChange}
                  options={yearOptions}
                />
                <Text type="secondary">
                  Дни в будущем (текущий год) берутся из централизованного прогноза/нормы.
                </Text>
              </Space>
            )}
            {climateKind === 'random' && (
              <Space direction="vertical" style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message="Возмущённый исторический год"
                  description="Реальный год локации с коррелированными суточными отклонениями (AR(1)) — правдоподобная, но новая погода. Не усреднение."
                />
                <Input
                  style={{ width: 260 }}
                  placeholder="seed (необязательно)"
                  value={seed}
                  onChange={(e) => setSeed(e.target.value)}
                />
              </Space>
            )}
            {climateKind === 'userCustom' && (
              userMapsFetching ? (
                <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
              ) : userMaps.length === 0 ? (
                <Empty description="Нет сохранённых климатических карт для этой локации" />
              ) : (
                <Select
                  style={{ width: 360 }}
                  placeholder="Выберите свою карту"
                  value={customMapId ?? undefined}
                  onChange={(v) => setCustomMapId(v)}
                  options={userMaps.map((m) => ({ value: m.id, label: m.name }))}
                />
              )
            )}
          </div>
        </div>
      ),
    },
    {
      title: 'План',
      content: (
        <div>
          <Paragraph type="secondary">
            План — неизменяемая сохранённая раскладка полей, грядок и построек.
            Исходный сейв после capture больше не нужен для применения плана.
          </Paragraph>
          <Radio.Group
            value={fromPlan ? 'plan' : 'blank'}
            onChange={(e) => setFromPlan(e.target.value === 'plan')}
            optionType="button"
            buttonStyle="solid"
            options={[
              { label: 'С чистого участка', value: 'blank' },
              { label: 'Из сохранённого плана', value: 'plan' },
            ]}
          />
          {fromPlan && (
            <div style={{ marginTop: 16 }}>
              {plansFetching ? (
                <Spin />
              ) : gardenPlans.length === 0 ? (
                <Empty description="Нет сохранённых планов" />
              ) : (
                <Select
                  style={{ width: 360 }}
                  placeholder="Выберите план"
                  value={planVersionId ?? undefined}
                  onChange={(v) => setPlanVersionId(v)}
                  options={gardenPlans.map((plan) => ({
                    value: plan.latestVersionId,
                    label: `${plan.name} · ${plan.latestVersionSummary?.counts.beds ?? 0} грядок`,
                  }))}
                />
              )}
              {selectedPlan?.latestVersionSummary?.layers.plantings && (
                <div style={{ marginTop: 12 }}>
                  <Checkbox checked={applyPlantings} onChange={(e) => setApplyPlantings(e.target.checked)}>
                    Применить сохранённую схему посадок
                  </Checkbox>
                </div>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      title: 'Параметры',
      content: (
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div>
            <Text>Название сейва</Text>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          {fromPlan ? (
            <div>
              <Text>Год применения плана</Text>
              <Input
                type="number"
                min={1900}
                max={2200}
                value={planTargetYear}
                onChange={(e) => setPlanTargetYear(Number(e.target.value))}
              />
              <Text type="secondary">Сейв начнётся 1 января {planTargetYear} года.</Text>
            </div>
          ) : (
            <div>
              <Text>Дата старта</Text>
              <DatePicker
                style={{ width: '100%' }}
                value={startDate}
                onChange={(d) => d && setStartDate(d)}
                allowClear={false}
              />
            </div>
          )}
          <div>
            <Text>Система измерений</Text>
            <Segmented
              block
              options={MEASUREMENT_OPTIONS}
              value={fromPlan
                ? (selectedPlan?.latestVersionSummary?.planScale?.preset?.startsWith('imperial') ? 'imperial' : 'metric')
                : measurementSystem}
              disabled={fromPlan}
              onChange={(v) => setMeasurementSystem(v as string)}
            />
            <Text type="secondary">{fromPlan ? 'Физический масштаб наследуется из плана.' : 'После создания изменить нельзя.'}</Text>
          </div>
          <div>
            <Text>Описание</Text>
            <Input.TextArea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title="Новый сейв — дружелюбное создание"
      open={open}
      onCancel={handleClose}
      width={720}
      footer={
        <Space>
          <Button onClick={handleClose}>Отмена</Button>
          {step > 0 && <Button onClick={() => setStep((s) => s - 1)}>Назад</Button>}
          {step < steps.length - 1 ? (
            <Button type="primary" disabled={!canNext} onClick={() => setStep((s) => s + 1)}>
              Далее
            </Button>
          ) : (
            <Button type="primary" loading={creating} disabled={!locationKey} onClick={handleCreate}>
              Создать
            </Button>
          )}
        </Space>
      }
    >
      <Steps current={step} size="small" items={steps.map((s) => ({ title: s.title }))} style={{ marginBottom: 20 }} />
      <div style={{ minHeight: 320 }}>{steps[step].content}</div>
    </Modal>
  );
};

export default GuidedSaveCreationWizard;
